import React, { useContext, useState, useRef } from "react";
import useCopyToClipboard from "../../hooks/useCopyToClipboard";
import { darkOrLightColor } from "../../helpers/darkOrLightColor";
import LinkToDetailsColor from "../LinkToDetailsColor";
import { ContextLocale } from "../../context/ContextLocale";
import CopyColorMessage from "../CopyColorMessage";
import {
  color_wrapper,
  color_code,
  container_options,
  button_option,
  button_copy,
  lock_button,
} from "../../styles/modules/color.module.css";

const TEXTS = {
  es: {
    copy: "copiar",
    lock: "bloquear",
    unlock: "desbloquear",
    edit: "editar",
  },
  en: {
    copy: "copy",
    lock: "lock",
    unlock: "unlock",
    edit: "edit",
  },
  pt: {
    copy: "copiar",
    lock: "bloquear",
    unlock: "desbloquear",
    edit: "editar",
  },
  fr: {
    copy: "copier",
    lock: "verrouiller",
    unlock: "déverrouiller",
    edit: "éditer",
  },
  de: {
    copy: "kopieren",
    lock: "sperren",
    unlock: "entsperren",
    edit: "bearbeiten",
  },
};

function Color({
  color,
  path,
  index,
  withLinkColor = false,
  isLocked = false,
  onToggleLock,
  onChangeColor,
}) {
  const { lang } = useContext(ContextLocale);
  const [message, showMessage, handleCopy] = useCopyToClipboard(`${color}`);
  const [isEditing, setIsEditing] = useState(false);
  const colorInputRef = useRef(null);

  const texts = TEXTS[lang] || TEXTS.en;
  const textColor = darkOrLightColor(color);

  const handleColorInputChange = e => {
    if (onChangeColor) {
      onChangeColor(e.target.value);
    }
  };

  const handleEditClick = e => {
    e.stopPropagation();
    setIsEditing(true);
    setTimeout(() => colorInputRef.current?.click(), 0);
  };

  return (
    <>
      {showMessage && <CopyColorMessage colorHex={color} message={message} />}

      <div
        className={color_wrapper}
        style={{ backgroundColor: color }}
        onClick={handleCopy}
      >
        {/* Botón de bloqueo */}
        {onToggleLock && (
          <button
            className={lock_button}
            onClick={e => {
              e.stopPropagation();
              onToggleLock();
            }}
            title={isLocked ? texts.unlock : texts.lock}
            style={{ color: textColor }}
          >
            {isLocked ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 9.9-1" />
              </svg>
            )}
          </button>
        )}

        {/* Número de tecla de atajo */}
        {index !== undefined && (
          <span
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              background: "rgba(0,0,0,0.2)",
              color: textColor,
              padding: "2px 8px",
              borderRadius: "4px",
              fontSize: "0.75rem",
              fontFamily: "SF Mono, monospace",
              backdropFilter: "blur(4px)",
            }}
          >
            {index + 1}
          </span>
        )}

        {withLinkColor && (
          <div className={container_options}>
            <div onClick={e => e.stopPropagation()}>
              <LinkToDetailsColor color={color} path={path} />
            </div>

            {/* Botón editar color */}
            <button
              onClick={handleEditClick}
              style={{
                boxShadow: `0px 0px 3px 0px ${textColor}`,
                color: textColor,
                background: color,
              }}
              className={`${button_option} ${button_copy}`}
            >
              <svg
                viewBox="0 0 24 24"
                width="17"
                height="17"
                fill="none"
                stroke={textColor}
                strokeWidth="2"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <strong>{texts.edit}</strong>
            </button>

            {/* Botón copiar */}
            <button
              style={{
                boxShadow: `0px 0px 3px 0px ${textColor}`,
                color: textColor,
                background: color,
              }}
              className={`${button_option} ${button_copy}`}
            >
              <svg
                viewBox="0 0 24 24"
                width="17"
                height="17"
                fill="none"
                stroke={textColor}
                strokeWidth="2"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              <strong>{texts.copy}</strong>
            </button>

            {/* Input de color oculto */}
            <input
              ref={colorInputRef}
              type="color"
              value={color}
              onChange={handleColorInputChange}
              onClick={e => e.stopPropagation()}
              style={{
                position: "absolute",
                opacity: 0,
                width: 0,
                height: 0,
              }}
            />
          </div>
        )}

        <strong style={{ color: textColor }} className={color_code}>
          {isLocked && (
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke={textColor}
              strokeWidth="2"
              style={{ marginRight: "6px" }}
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          )}
          {color.toUpperCase()}
        </strong>
      </div>
    </>
  );
}

export default Color;
